import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { usePdaScan, isInPdaWrapper } from "../../hooks/usePdaScan";
import { usePdaScanReset } from "../../hooks/usePdaScanReset";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, AlertTriangle, ChevronRight, Trash2, Save, History, Building2, Package, Clock, ClipboardCheck, ScanLine, X, RotateCcw, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 300;
const EMPTY_COL_FILTERS = { note: [], products: [], statuses: [], locations: [] };
const appendColFilters = (params, cf) => {
  (cf.note || []).forEach((v) => params.append("note", v));
  (cf.products || []).forEach((v) => params.append("product_ids", v));
  (cf.statuses || []).forEach((v) => params.append("statuses", v));
  if ((cf.locations || []).length) params.append("locations", cf.locations.join(","));
};

const SUBTABS = [
  { key: "overview", label: "Overview", icon: Package },
  { key: "hotels",   label: "Hotels",   icon: Building2 },
  { key: "categories", label: "Categories", icon: Package },
  { key: "overdue",  label: "Overdue",  icon: Clock },
  { key: "identify",  label: "Identify", icon: Search },
  { key: "stocktake", label: "Stocktake", icon: ClipboardCheck },
];

export default function InventoryTab({ products = [] }) {
  const [tab, setTab] = useState("overview");
  const [hotelsList, setHotelsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Bulk register state
  const [productId, setProductId] = useState("");
  const [codesText, setCodesText] = useState("");
  const [registerNotes, setRegisterNotes] = useState("");
  const [registerBurstInput, setRegisterBurstInput] = useState("");

  // Bulk delete state
  const [delCodesText, setDelCodesText] = useState("");
  const [delReason, setDelReason] = useState("damaged");
  const [delConfirming, setDelConfirming] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [delBurstInput, setDelBurstInput] = useState("");

  // Append a value (or values) from a PDA burst input as new lines into a textarea.
  const appendBurst = (val, setText) => {
    const v = String(val || "").trim();
    if (!v) return;
    setText((prev) => (prev && !prev.endsWith("\n") ? prev + "\n" : prev) + v);
  };

  // Bulk-register / bulk-delete also reset their textareas on Reset.
  usePdaScanReset(() => {
    if (registerOpen) setCodesText("");
    if (bulkDeleteOpen) { setDelCodesText(""); setDelConfirming(false); }
  }, { enabled: registerOpen || bulkDeleteOpen });

  // Native PDA wrapper: route every "Stop & Send" payload to whichever bulk
  // dialog is currently open. Multiple dialogs are mutually exclusive, so
  // pick whichever is `open`.
  usePdaScan((epcs) => {
    const sink = registerOpen ? setCodesText
               : bulkDeleteOpen ? (u) => { setDelCodesText(u); setDelConfirming(false); }
               : null;
    if (!sink) return;
    sink((prev) => {
      const sep = prev && !prev.endsWith("\n") ? "\n" : "";
      return prev + sep + epcs.join("\n");
    });
  }, { enabled: registerOpen || bulkDeleteOpen });

  // Bumped after any mutation so sub-views (HotelDetail, CategoryDetail, OverdueView)
  // know to re-fetch their own item lists. Aggregates alone are not enough — the
  // currently-open sub-view holds its own items[] state.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  const refreshAggregates = async () => {
    try {
      const [hRes, pRes, overdueRes] = await Promise.all([
        axios.get(`${API}/linen/by-hotel`),
        axios.get(`${API}/linen/by-product`),
        axios.get(`${API}/linen?overdue=true&page=1&page_size=1`),
      ]);
      setHotelsList(hRes.data || []);
      setProductsList(pRes.data || []);
      setOverdueCount(overdueRes.data?.total || (overdueRes.data?.items || []).length);
    } catch (e) {
      toast.error("Failed to load inventory");
    }
  };

  useEffect(() => { refreshAggregates(); }, []);

  const parseCodes = (text) => {
    const out = [];
    text.split(/[,\n\s]+/).map((c) => c.trim()).filter(Boolean).forEach((c) => {
      const m = c.match(/^(\D*?)(\d+)-(\d+)$/);
      if (m) {
        const prefix = m[1] || "";
        const a = parseInt(m[2], 10);
        const b = parseInt(m[3], 10);
        const pad = m[2].length;
        for (let i = a; i <= b; i++) out.push(prefix + String(i).padStart(pad, "0"));
      } else {
        out.push(c);
      }
    });
    return [...new Set(out)];
  };

  const submitRegister = async () => {
    if (!productId) { toast.error("Pick a product"); return; }
    const codes = parseCodes(codesText);
    if (codes.length === 0) { toast.error("No codes parsed"); return; }
    try {
      const res = await axios.post(`${API}/linen/bulk-register`, {
        product_id: productId, codes, notes: registerNotes || null,
      });
      toast.success(`Registered ${res.data.registered}, skipped ${res.data.skipped_already_exists}`);
      setRegisterOpen(false);
      setCodesText(""); setProductId(""); setRegisterNotes("");
      refreshAggregates();
      bumpRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitBulkDelete = async () => {
    const codes = parseCodes(delCodesText);
    if (codes.length === 0) { toast.error("No codes parsed"); return; }
    // Two-step confirmation rendered inside the Dialog itself (window.confirm
    // can be silently blocked by some embedded browsers / preview iframes).
    if (!delConfirming) { setDelConfirming(true); return; }
    setDelLoading(true);
    try {
      const res = await axios.post(`${API}/linen/bulk-delete`, {
        rfids: codes, reason: delReason || null, cost_recovered: false,
      });
      toast.success(`Deleted ${res.data.deleted_count}`, {
        description: res.data.not_found.length ? `Not found: ${res.data.not_found.length}` : undefined,
      });
      setBulkDeleteOpen(false);
      setDelConfirming(false);
      setDelCodesText("");
      refreshAggregates();
      bumpRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setDelLoading(false);
    }
  };

  // Reset confirm state when dialog closes
  const closeBulkDelete = () => {
    setBulkDeleteOpen(false);
    setDelConfirming(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Inventory</h2>
          <p className="text-sm text-muted-foreground mt-1">Registered linen items and their current location.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="inventory-bulk-delete-btn"
            onClick={() => setBulkDeleteOpen(true)}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Bulk delete
          </Button>
          <Button data-testid="inventory-register-btn" onClick={() => setRegisterOpen(true)} className="bg-brand-navy hover:bg-brand-navy-hover">
            <Plus className="h-4 w-4 mr-2" /> Bulk register
          </Button>
        </div>
      </div>

      {/* Sub-tabs — horizontally scrollable on small screens (PDA portrait) */}
      <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto scrollbar-thin">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const badge = t.key === "overdue" ? overdueCount : null;
          return (
            <button
              key={t.key}
              data-testid={`inventory-subtab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium flex items-center gap-1.5 border-b-2 transition-colors -mb-px ${
                active ? "border-brand-navy text-brand-navy" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
              {badge !== null && badge > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewView hotelsList={hotelsList} productsList={productsList} overdueCount={overdueCount} onJump={setTab} />}
      {tab === "hotels"   && <HotelsView hotelsList={hotelsList} onUpdate={() => { refreshAggregates(); bumpRefresh(); }} refreshKey={refreshKey} />}
      {tab === "categories" && <CategoriesView productsList={productsList} hotelsList={hotelsList} onUpdate={() => { refreshAggregates(); bumpRefresh(); }} refreshKey={refreshKey} />}
      {tab === "overdue"  && <OverdueView hotelsList={hotelsList} onUpdate={() => { refreshAggregates(); bumpRefresh(); }} refreshKey={refreshKey} />}
      {tab === "identify" && <IdentifyView onUpdate={() => { refreshAggregates(); bumpRefresh(); }} />}
      {tab === "stocktake" && <StocktakeView onUpdate={() => { refreshAggregates(); bumpRefresh(); }} />}

      {/* Bulk register dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy flex items-center gap-2">
              <Plus className="h-5 w-5" /> Bulk Register RFIDs
            </DialogTitle>
            <DialogDescription>
              Pick a product, then list the RFID codes. One per line, comma-separated, or a range like <code>001-100</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Product</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger><SelectValue placeholder="Choose product" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">RFID codes</Label>
              <div className="mb-2">
                <Input
                  data-testid="inventory-register-burst-input"
                  value={registerBurstInput}
                  onChange={(e) => setRegisterBurstInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      appendBurst(registerBurstInput, setCodesText);
                      setRegisterBurstInput("");
                    }
                  }}
                  placeholder="🔫 PDA burst — pull trigger, codes flow below…"
                  className="font-mono text-sm bg-emerald-50/40 border-emerald-200"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
              <Textarea
                data-testid="inventory-register-codes"
                value={codesText}
                onChange={(e) => setCodesText(e.target.value)}
                placeholder={"001\n002\n003  (or)  001-100"}
                rows={6}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Will register {parseCodes(codesText).length} codes.
              </p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Notes (optional)</Label>
              <Input
                data-testid="inventory-register-notes"
                value={registerNotes}
                onChange={(e) => setRegisterNotes(e.target.value)}
                placeholder="e.g. supplier ABC, 250gsm cotton"
              />
              <p className="text-xs text-muted-foreground mt-1">Applied to every newly-inserted RFID.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterOpen(false)}>Cancel</Button>
            <Button data-testid="inventory-register-submit" onClick={submitRegister} className="bg-brand-navy hover:bg-brand-navy-hover">
              Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(v) => (v ? setBulkDeleteOpen(true) : closeBulkDelete())}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-red-700 flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Bulk Delete RFIDs
            </DialogTitle>
            <DialogDescription>
              Permanently remove RFIDs from inventory (e.g. damaged or lost linen). Writes to audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">RFID codes</Label>
              <div className="mb-2">
                <Input
                  data-testid="inventory-delete-burst-input"
                  value={delBurstInput}
                  onChange={(e) => setDelBurstInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      appendBurst(delBurstInput, (updater) => {
                        setDelCodesText(updater);
                        setDelConfirming(false);
                      });
                      setDelBurstInput("");
                    }
                  }}
                  placeholder="🔫 PDA burst — pull trigger, codes flow below…"
                  className="font-mono text-sm bg-emerald-50/40 border-emerald-200"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  disabled={delConfirming || delLoading}
                />
              </div>
              <Textarea
                data-testid="inventory-delete-codes"
                value={delCodesText}
                onChange={(e) => { setDelCodesText(e.target.value); setDelConfirming(false); }}
                placeholder={"001\n002  (or)  001-010"}
                rows={6}
                className="font-mono"
                disabled={delConfirming || delLoading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Will delete {parseCodes(delCodesText).length} codes.
              </p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Reason</Label>
              <Select value={delReason} onValueChange={setDelReason} disabled={delConfirming || delLoading}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                  <SelectItem value="end-of-life">End of life</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {delConfirming && (
              <div
                data-testid="inventory-delete-confirm-banner"
                className="border border-red-300 bg-red-50 rounded-md px-4 py-3 text-sm text-red-800"
              >
                <p className="font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Confirm deletion
                </p>
                <p className="mt-1">
                  You&apos;re about to permanently remove{" "}
                  <span className="font-mono font-bold">{parseCodes(delCodesText).length}</span> RFID(s).
                  This cannot be undone. Click <span className="font-semibold">Confirm delete</span> below to proceed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBulkDelete} disabled={delLoading}>Cancel</Button>
            <Button
              onClick={submitBulkDelete}
              data-testid="inventory-delete-submit"
              disabled={delLoading || parseCodes(delCodesText).length === 0}
              className="bg-red-700 hover:bg-red-800 text-white"
            >
              {delLoading
                ? "Deleting..."
                : delConfirming
                ? `Confirm delete (${parseCodes(delCodesText).length})`
                : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Sub-views ----------
function StatCard({ label, value, tone, onClick }) {
  const toneClass = tone === "green" ? "text-green-700" : tone === "amber" ? "text-amber-700" : tone === "red" ? "text-red-700" : "text-brand-navy";
  return (
    <Card className={onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""} onClick={onClick}>
      <CardContent className="p-6">
        <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">{label}</p>
        <p className={`font-display text-4xl font-extrabold mt-1 ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function OverviewView({ hotelsList, productsList, overdueCount, onJump }) {
  const total = productsList.reduce((s, p) => s + p.total, 0);
  const atWh = productsList.reduce((s, p) => s + p.at_warehouse, 0);
  const atHotel = productsList.reduce((s, p) => s + p.at_hotel, 0);
  const missingTotal = productsList.reduce((s, p) => s + (p.missing || 0), 0);
  // Sort alphabetically by product name (global rule — applies to every
  // product list across the app per user preference).
  const sortedProducts = [...productsList].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || ""));
  return (
    <div className="space-y-6">
      <div className={`grid grid-cols-1 md:grid-cols-${missingTotal > 0 ? 5 : 4} gap-4`}>
        <StatCard label="Total registered" value={total} />
        <StatCard label="At warehouse" value={atWh} tone="green" />
        <StatCard label="Out at hotels" value={atHotel} tone="amber" onClick={() => onJump("hotels")} />
        <StatCard label="Overdue" value={overdueCount} tone="red" onClick={() => onJump("overdue")} />
        {missingTotal > 0 && <StatCard label="Missing" value={missingTotal} tone="red" />}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">My stock — by product, by location</CardTitle></CardHeader>
        <CardContent>
          {sortedProducts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No products registered yet.</p>
          ) : (
            <div className="space-y-2">
              {sortedProducts.map((p) => {
                const total = p.total || 0;
                const wh = p.at_warehouse || 0;
                const hotel = p.at_hotel || 0;
                const missing = p.missing || 0;
                const whPct = total > 0 ? (wh / total) * 100 : 0;
                const hotelPct = total > 0 ? (hotel / total) * 100 : 0;
                const missingPct = total > 0 ? (missing / total) * 100 : 0;
                return (
                  <div key={p.product_id} className="rounded-lg border bg-white p-3" data-testid={`overview-product-${p.product_id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.product_color || "#888" }} />
                        <span className="font-display font-bold text-base truncate">{p.product_name}</span>
                      </div>
                      <span className="font-mono font-extrabold text-2xl tabular-nums">{total}</span>
                    </div>
                    <div className={`grid ${missing > 0 ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
                      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">At warehouse</div>
                        <div className="flex items-baseline justify-between">
                          <span className="font-mono font-extrabold text-2xl text-emerald-900">{wh}</span>
                          <span className="text-xs text-emerald-800">{whPct.toFixed(0)}%</span>
                        </div>
                      </div>
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">At hotels</div>
                        <div className="flex items-baseline justify-between">
                          <span className="font-mono font-extrabold text-2xl text-amber-900">{hotel}</span>
                          <span className="text-xs text-amber-800">{hotelPct.toFixed(0)}%</span>
                        </div>
                      </div>
                      {missing > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2" data-testid={`overview-missing-${p.product_id}`}>
                          <div className="text-[10px] uppercase tracking-wider text-red-800 font-bold">Missing</div>
                          <div className="flex items-baseline justify-between">
                            <span className="font-mono font-extrabold text-2xl text-red-700">{missing}</span>
                            <span className="text-xs text-red-700">{missingPct.toFixed(0)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Proportional bar */}
                    {total > 0 && (
                      <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-neutral-200 flex">
                        <div className="bg-emerald-500" style={{ width: `${whPct}%` }} />
                        <div className="bg-amber-500" style={{ width: `${hotelPct}%` }} />
                        <div className="bg-red-500" style={{ width: `${missingPct}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function HotelsView({ hotelsList, onUpdate, refreshKey }) {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  if (selected) return <HotelDetail hotel={selected} onBack={() => { setSelected(null); onUpdate(); }} onUpdate={onUpdate} refreshKey={refreshKey} />;
  const q = search.trim().toLowerCase();
  const filtered = (hotelsList || [])
    .filter((h) => !q || (h.hotel_name || "").toLowerCase().includes(q))
    .sort((a, b) => (a.hotel_name || "").localeCompare(b.hotel_name || ""));
  return (
    <div className="space-y-3">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          data-testid="inventory-hotels-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search hotel…"
          className="pl-9"
        />
      </div>
      {hotelsList.length === 0 && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">No items currently at hotels.</CardContent></Card>
      )}
      {hotelsList.length > 0 && filtered.length === 0 && (
        <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">No hotel matches "{search}"</CardContent></Card>
      )}
      {filtered.map((h) => (
        <Card
          key={h.hotel_id || h.hotel_name}
          data-testid={`hotel-card-${h.hotel_id}`}
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setSelected(h)}
        >
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <HotelThumb name={h.hotel_name} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{h.hotel_name}</p>
                <p className="text-xs text-muted-foreground">{h.items_held} items currently held</p>
                {h.products && h.products.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`hotel-products-${h.hotel_id}`}>
                    {h.products.slice(0, 6).map((p) => (
                      <span
                        key={p.product_id}
                        className="inline-flex items-center gap-1 rounded-full bg-neutral-100 border border-neutral-200 pl-1 pr-2 py-0.5 text-[11px]"
                        title={`${p.product_name} · ${p.count}`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: p.product_color || "#888" }} />
                        <span className="font-medium truncate max-w-[110px]">{p.product_name}</span>
                        <span className="font-mono font-bold text-neutral-700">{p.count}</span>
                      </span>
                    ))}
                    {h.products.length > 6 && (
                      <span className="text-[10px] text-muted-foreground">+ {h.products.length - 6} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {h.overdue_count > 0 && (
                <Badge className="bg-red-100 text-red-700 border border-red-200 font-mono">
                  {h.overdue_count} overdue
                </Badge>
              )}
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Deterministic colour-pill avatar for a hotel — first two initials over a
 *  saturated background derived from a stable hash of the name. Gives every
 *  hotel a recognisable "thumbnail" without needing uploaded logos. */
function HotelThumb({ name }) {
  const label = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
  // Lightweight string hash → hue (0–359).
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = ((hash << 5) - hash) + name.charCodeAt(i);
  const hue = Math.abs(hash) % 360;
  const bg = `hsl(${hue}, 60%, 38%)`;
  return (
    <div
      className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center font-display font-bold text-white text-base"
      style={{ background: bg }}
      aria-hidden
    >
      {label}
    </div>
  );
}

function HotelDetail({ hotel, onBack, onUpdate, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("rfid_asc");
  const [colFilters, setColFilters] = useState(EMPTY_COL_FILTERS);
  const [facets, setFacets] = useState(null);
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [detailRfid, setDetailRfid] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Group-by-product overview: load up to 2000 items at this hotel once and
  // aggregate locally so the operator sees big "10 sheets / 6 towels" blocks
  // and only drills into a flat RFID list after picking a product.
  const [productBreakdown, setProductBreakdown] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const loadBreakdown = async () => {
    setBreakdownLoading(true);
    try {
      const params = new URLSearchParams({ hotel_id: hotel.hotel_id || "" });
      // Disambiguate legacy rows where multiple hotels share a user_id-as-hotel_id.
      if (hotel.hotel_name) params.append("hotel_name", hotel.hotel_name);
      const res = await axios.get(`${API}/linen/hotel-breakdown?${params}`);
      setProductBreakdown(res.data || []);
    } catch (e) {
      toast.error("Failed to load product breakdown");
    } finally {
      setBreakdownLoading(false);
    }
  };
  useEffect(() => { loadBreakdown(); /* eslint-disable-next-line */ }, [hotel.hotel_id, refreshKey]);

  const load = async () => {
    try {
      const params = new URLSearchParams({
        hotel_id: hotel.hotel_id || "",
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort,
      });
      if (hotel.hotel_name) params.append("hotel_name", hotel.hotel_name);
      if (search) params.append("search", search);
      if (showOverdueOnly) params.append("overdue", "true");
      if (selectedProductId && selectedProductId !== "_unknown") params.append("product_id", selectedProductId);
      appendColFilters(params, colFilters);
      const res = await axios.get(`${API}/linen?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error("Failed to load hotel items");
    }
  };
  useEffect(() => {
    if (selectedProductId) load();
    /* eslint-disable-next-line */
  }, [hotel.hotel_id, page, search, sort, colFilters, showOverdueOnly, selectedProductId, refreshKey]);

  const loadFacets = async () => {
    try {
      const params = new URLSearchParams({ hotel_id: hotel.hotel_id || "" });
      if (hotel.hotel_name) params.append("hotel_name", hotel.hotel_name);
      if (selectedProductId && selectedProductId !== "_unknown") params.append("product_id", selectedProductId);
      const res = await axios.get(`${API}/linen/facets?${params}`);
      setFacets(res.data);
    } catch (e) { /* facets are non-critical */ }
  };
  useEffect(() => {
    if (selectedProductId) loadFacets();
    /* eslint-disable-next-line */
  }, [hotel.hotel_id, selectedProductId, refreshKey]);

  const overdueItems = items.filter((i) => i.is_overdue);
  const selectedOverdueOnly = [...selected].every((rfid) => items.find((i) => i.rfid === rfid)?.is_overdue);

  const [resetting, setResetting] = useState(false);

  const resetClock = async () => {
    if (selected.size === 0 || resetting) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/linen/reset-clock`, { rfids: [...selected] });
      toast.success(`Overdue clock reset for ${res.data.reset_count} item(s) — they start fresh from today`);
      setSelected(new Set());
      load(); loadBreakdown(); onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const onDeleteClick = () => {
    if (selected.size === 0) return;
    if (!selectedOverdueOnly) {
      toast.error("This action removes overdue items only — use Bulk delete on the main toolbar otherwise.");
      return;
    }
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/linen/bulk-delete`, {
        rfids: [...selected],
        reason: `overdue at ${hotel.hotel_name}`,
        cost_recovered: true,
      });
      toast.success(`Deleted ${res.data.deleted_count} overdue item(s)`);
      setSelected(new Set());
      setConfirmOpen(false);
      load(); loadBreakdown(); onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // ── product picker view (default) ──
  if (!selectedProductId) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Button variant="ghost" onClick={onBack} className="mb-1 -ml-3">← Back to hotels</Button>
            <h3 className="font-display text-xl font-bold text-brand-navy">{hotel.hotel_name}</h3>
            <p className="text-sm text-muted-foreground">
              {hotel.items_held} items · {hotel.overdue_count} overdue
            </p>
          </div>
        </div>

        {breakdownLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : productBreakdown.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No items at this hotel.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hotel-product-blocks">
            {productBreakdown.map((p) => (
              <button
                key={p.product_id}
                onClick={() => { setSelectedProductId(p.product_id); setPage(1); setSelected(new Set()); }}
                data-testid={`hotel-product-block-${p.product_id}`}
                className="rounded-lg border bg-white p-4 text-left hover:border-amber-400 hover:shadow-sm active:bg-amber-50 transition"
              >
                <div className="flex items-center gap-2 mb-2 min-w-0">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.product_color || "#888" }} />
                  <span className="font-display font-bold text-sm truncate">{p.product_name}</span>
                </div>
                <div className="font-mono font-extrabold text-4xl tabular-nums leading-tight">{p.count}</div>
                {p.overdue > 0 && (
                  <Badge className="mt-1 bg-red-100 text-red-700 border border-red-200">{p.overdue} overdue</Badge>
                )}
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2">Tap for details</div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── flat RFID list view (drilled into one product) ──
  const currentProduct = productBreakdown.find((p) => p.product_id === selectedProductId);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" onClick={() => { setSelectedProductId(null); setSelected(new Set()); setSearch(""); setShowOverdueOnly(false); }} className="mb-1 -ml-3">
            ← Back to {hotel.hotel_name}
          </Button>
          <h3 className="font-display text-xl font-bold text-brand-navy flex items-center gap-2">
            {currentProduct ? <span className="w-3 h-3 rounded-full" style={{ background: currentProduct.product_color || "#888" }} /> : null}
            {currentProduct?.product_name || "Items"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {currentProduct?.count ?? 0} items{(currentProduct?.overdue ?? 0) > 0 ? ` · ${currentProduct.overdue} overdue` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Search RFID"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-48"
          />
          <Button
            variant={showOverdueOnly ? "default" : "outline"}
            onClick={() => { setShowOverdueOnly((v) => !v); setPage(1); }}
            data-testid="hotel-overdue-toggle"
          >
            <Clock className="h-4 w-4 mr-2" />
            {showOverdueOnly ? "Showing overdue only" : "Show overdue only"}
          </Button>
          {selected.size > 0 && (
            <Button
              onClick={resetClock}
              disabled={resetting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="hotel-reset-overdue-clock"
              title="Restart the overdue clock for the selected items (last scanned = now)"
            >
              <Clock className="h-4 w-4 mr-2" /> {resetting ? "Resetting…" : `Clear overdue ${selected.size}`}
            </Button>
          )}
          {selected.size > 0 && (
            <Button
              onClick={onDeleteClick}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="hotel-delete-overdue"
              disabled={!selectedOverdueOnly}
              title={selectedOverdueOnly ? "Delete selected overdue items" : "Only overdue items can be deleted from here"}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete {selected.size} (overdue)
            </Button>
          )}
        </div>
      </div>

      <RfidTable
        items={items}
        total={total}
        page={page}
        onPageChange={setPage}
        selected={selected}
        onSelectChange={setSelected}
        onOpenDetail={setDetailRfid}
        sort={sort}
        onSortChange={(s) => { setSort(s); setPage(1); }}
        facets={facets}
        colFilters={colFilters}
        onColFilterChange={(col, vals) => { setColFilters((f) => ({ ...f, [col]: vals })); setPage(1); }}
        emptyMessage={showOverdueOnly ? "No overdue items for this product." : "No items for this product."}
      />

      {overdueItems.length > 0 && !showOverdueOnly && (
        <p className="text-xs text-amber-700">
          <AlertTriangle className="inline h-3 w-3 mr-1" />
          {overdueItems.length} on this page are overdue — flip the toggle to focus on them.
        </p>
      )}

      <RfidDetailDialog rfid={detailRfid} onClose={() => setDetailRfid(null)} onUpdated={() => { load(); loadBreakdown(); }} />
      <CostRecoveredConfirm
        open={confirmOpen}
        count={selected.size}
        hotelName={hotel.hotel_name}
        loading={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function CategoriesView({ productsList, hotelsList, onUpdate, refreshKey }) {
  const [selected, setSelected] = useState(null);
  if (selected) return <ProductDetail product={selected} hotelsList={hotelsList} onBack={() => { setSelected(null); onUpdate(); }} onUpdate={onUpdate} refreshKey={refreshKey} />;
  return (
    <div className="space-y-3">
      {productsList.map((p) => (
        <Card key={p.product_id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(p)}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full" style={{ background: p.product_color }} />
              <div>
                <p className="font-semibold">{p.product_name}</p>
                <p className="text-xs text-muted-foreground">
                  {p.total} registered · {p.at_warehouse} at warehouse · {p.at_hotel} at hotels
                  {p.missing > 0 && <span className="text-red-600 font-bold"> · {p.missing} missing</span>}
                  {p.overdue_days ? ` · overdue after ${p.overdue_days}d` : " · overdue tracking off"}
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ProductDetail({ product, hotelsList = [], onBack, onUpdate, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("note_asc");
  const [colFilters, setColFilters] = useState(EMPTY_COL_FILTERS);
  const [facets, setFacets] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [detailRfid, setDetailRfid] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadFacets = async () => {
    try {
      const res = await axios.get(`${API}/linen/facets?product_id=${encodeURIComponent(product.product_id)}`);
      setFacets(res.data);
    } catch (e) { /* facets are non-critical */ }
  };
  useEffect(() => { loadFacets(); /* eslint-disable-next-line */ }, [product.product_id, refreshKey]);

  const load = async () => {
    try {
      const params = new URLSearchParams({
        product_id: product.product_id,
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort,
      });
      if (search) params.append("search", search);
      appendColFilters(params, colFilters);
      const res = await axios.get(`${API}/linen?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error("Failed to load items");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product.product_id, page, search, sort, colFilters, refreshKey]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/linen/bulk-delete`, {
        rfids: [...selected],
        reason: `bulk-delete from product ${product.product_name}`,
        cost_recovered: false,
      });
      toast.success(`Deleted ${res.data.deleted_count} RFID(s)`);
      setSelected(new Set());
      setConfirmOpen(false);
      load(); onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" onClick={onBack} className="mb-1 -ml-3">← Back to categories</Button>
          <h3 className="font-display text-xl font-bold text-brand-navy flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: product.product_color }} />
            {product.product_name}
          </h3>
          <p className="text-sm text-muted-foreground">
            {product.total} total · {product.at_warehouse} at warehouse · {product.at_hotel} at hotels
            {product.missing > 0 && <span className="text-red-600 font-bold"> · {product.missing} missing</span>}
            {product.overdue_days ? ` · overdue after ${product.overdue_days} days` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Search RFID or note"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-56"
            data-testid="product-search"
          />
          {selected.size > 0 && (
            <Button
              onClick={() => setConfirmOpen(true)}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="product-bulk-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete {selected.size} selected
            </Button>
          )}
        </div>
      </div>

      <RfidTable
        items={items}
        total={total}
        page={page}
        onPageChange={setPage}
        selected={selected}
        onSelectChange={setSelected}
        onOpenDetail={setDetailRfid}
        sort={sort}
        onSortChange={(s) => { setSort(s); setPage(1); }}
        facets={facets}
        colFilters={colFilters}
        onColFilterChange={(col, vals) => { setColFilters((f) => ({ ...f, [col]: vals })); setPage(1); }}
        emptyMessage="No RFIDs registered for this product yet."
      />

      <RfidDetailDialog rfid={detailRfid} onClose={() => setDetailRfid(null)} onUpdated={load} />

      <AlertDialog open={confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Delete {selected.size} RFID{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-base mt-2">
                <div>
                  This permanently removes the selected RFID{selected.size === 1 ? "" : "s"} for{" "}
                  <span className="font-semibold">{product.product_name}</span> from inventory.
                </div>
                <div className="text-sm text-muted-foreground">
                  History entries remain in audit logs. This action cannot be undone.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-bulk-delete-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="product-bulk-delete-confirm"
            >
              {deleting ? "Deleting…" : `Delete ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function OverdueView({ hotelsList = [], onUpdate, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("scanned_asc"); // oldest last-seen first = most suspicious first
  const [colFilters, setColFilters] = useState(EMPTY_COL_FILTERS);
  const [facets, setFacets] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [detailRfid, setDetailRfid] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadFacets = async () => {
    try {
      const res = await axios.get(`${API}/linen/facets?overdue=true`);
      setFacets(res.data);
    } catch (e) { /* facets are non-critical */ }
  };
  useEffect(() => { loadFacets(); /* eslint-disable-next-line */ }, [refreshKey]);

  const load = async () => {
    try {
      const params = new URLSearchParams({ overdue: "true", page: String(page), page_size: String(PAGE_SIZE), sort });
      appendColFilters(params, colFilters);
      const res = await axios.get(`${API}/linen?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || (res.data.items || []).length);
    } catch (e) {
      toast.error("Failed to load overdue items");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, sort, colFilters, refreshKey]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/linen/bulk-delete`, {
        rfids: [...selected], reason: "overdue write-off", cost_recovered: true,
      });
      toast.success(`Deleted ${res.data.deleted_count} overdue item(s)`);
      setSelected(new Set());
      setConfirmOpen(false);
      load(); onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" /> Overdue items
          </h3>
          <p className="text-sm text-muted-foreground">
            Items at a hotel past their product&apos;s threshold. Set per-product threshold in Products tab.
          </p>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-block" data-testid="admin-overdue-disclaimer">
            Overdue is an estimate only — it does not automatically incur charges, but these items deserve attention.
          </p>
        </div>
        {selected.size > 0 && (
          <Button
            onClick={() => setConfirmOpen(true)}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="overdue-delete"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete {selected.size} from system
          </Button>
        )}
      </div>
      <RfidTable
        items={items}
        total={total}
        page={page}
        onPageChange={setPage}
        selected={selected}
        onSelectChange={setSelected}
        onOpenDetail={setDetailRfid}
        sort={sort}
        onSortChange={(s) => { setSort(s); setPage(1); }}
        facets={facets}
        colFilters={colFilters}
        onColFilterChange={(col, vals) => { setColFilters((f) => ({ ...f, [col]: vals })); setPage(1); }}
        emptyMessage="No overdue items right now."
      />
      <RfidDetailDialog rfid={detailRfid} onClose={() => setDetailRfid(null)} onUpdated={load} />
      <CostRecoveredConfirm
        open={confirmOpen}
        count={selected.size}
        hotelName={null}
        loading={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// Shared confirm dialog used by Overdue + HotelDetail flows.
function CostRecoveredConfirm({ open, count, hotelName, loading, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !loading && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" /> Confirm overdue write-off
          </DialogTitle>
          <DialogDescription>
            About to permanently remove <span className="font-mono font-bold">{count}</span> overdue RFID(s){hotelName ? ` held by ${hotelName}` : ""}.
          </DialogDescription>
        </DialogHeader>
        <div className="px-1 py-2 text-sm">
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-amber-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Have you already recovered the cost?
            </p>
            <p className="mt-1">
              Make sure the hotel has paid for the lost linen before clicking <span className="font-semibold">Cost recovered — delete</span>.
              The deletion is permanent and writes an audit log entry.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button onClick={onConfirm} disabled={loading} className="bg-red-700 hover:bg-red-800 text-white" data-testid="cost-recovered-confirm">
            {loading ? "Deleting..." : "Cost recovered — delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reusable: RFID table + detail dialog ----------
function HeaderFilter({ options = [], selected = [], onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <span className="relative inline-block ml-1 align-middle" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`text-[11px] leading-none px-1 py-0.5 rounded border ${selected.length ? "bg-brand-navy text-white border-brand-navy" : "text-neutral-400 border-transparent hover:text-neutral-700 hover:border-neutral-300"}`}
        onClick={() => setOpen((o) => !o)}
        title="Filter this column"
        data-testid={testid}
      >
        ⏷{selected.length ? selected.length : ""}
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <span className="absolute left-0 z-50 mt-1 block w-64 rounded-md border bg-white shadow-lg p-2 max-h-80 overflow-auto font-normal normal-case" data-testid={`${testid}-menu`}>
            {options.length === 0 && <span className="block px-2 py-2 text-xs text-muted-foreground">No values</span>}
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-50 cursor-pointer text-sm">
                <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
            <span className="flex justify-between pt-2 border-t mt-1">
              <Button size="sm" variant="ghost" onClick={() => onChange([])}>Clear</Button>
              <Button size="sm" onClick={() => setOpen(false)}>Done</Button>
            </span>
          </span>
        </>
      )}
    </span>
  );
}

function SortableTh({ label, asc, desc, sort, onSortChange, className = "", filter = null }) {
  if (!onSortChange) return <th className={`px-4 py-3 font-semibold ${className}`}>{label}{filter}</th>;
  const active = sort === asc ? "asc" : sort === desc ? "desc" : null;
  return (
    <th className={`px-4 py-3 font-semibold whitespace-nowrap ${active ? "text-brand-navy" : ""} ${className}`}>
      <span
        className="cursor-pointer select-none hover:underline"
        onClick={() => onSortChange(active === "asc" ? desc : asc)}
        title={`Sort by ${label}`}
        data-testid={`sort-th-${asc}`}
      >
        {label} {active === "asc" ? "▲" : active === "desc" ? "▼" : <span className="text-neutral-300">↕</span>}
      </span>
      {filter}
    </th>
  );
}

// Multi-select location filter — tick one or several locations; combines
// with column sorting and search.
const STATUS_LABELS = { at_warehouse: "At warehouse", at_hotel: "At hotel", in_transit: "In transit" };

function daysSince(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function RfidTable({ items, total, page, onPageChange, selected, onSelectChange, onOpenDetail, emptyMessage, sort, onSortChange, facets, colFilters, onColFilterChange }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Ordinal offset for the "#" column so item #1 on page 2 shows as
  // (page-1)*PAGE_SIZE + 1 — a stable serial across pages, matching
  // the current sort order.
  const startNo = (page - 1) * PAGE_SIZE;
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.rfid));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) items.forEach((i) => next.delete(i.rfid));
    else items.forEach((i) => next.add(i.rfid));
    onSelectChange(next);
  };
  const toggle = (rfid) => {
    const next = new Set(selected);
    if (next.has(rfid)) next.delete(rfid);
    else next.add(rfid);
    onSelectChange(next);
  };
  return (
    <>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr>
              <th className="w-10 px-3 py-3">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
              </th>
              <th className="w-12 px-2 py-3 font-semibold text-right text-neutral-500">#</th>
              <SortableTh label="RFID" asc="rfid_asc" desc="rfid_desc" sort={sort} onSortChange={onSortChange} />
              <SortableTh label="Note" asc="note_asc" desc="note_desc" sort={sort} onSortChange={onSortChange}
                filter={facets && onColFilterChange ? (
                  <HeaderFilter testid="filter-note"
                    options={(facets.notes || []).map((n) => ({ value: n, label: n === "__none__" ? "(no note)" : n }))}
                    selected={colFilters.note} onChange={(v) => onColFilterChange("note", v)} />
                ) : null} />
              <SortableTh label="Product" asc="product_asc" desc="product_desc" sort={sort} onSortChange={onSortChange}
                filter={facets && onColFilterChange ? (
                  <HeaderFilter testid="filter-product"
                    options={(facets.products || []).map((p) => ({ value: p.id, label: p.name }))}
                    selected={colFilters.products} onChange={(v) => onColFilterChange("products", v)} />
                ) : null} />
              <SortableTh label="Status" asc="status_asc" desc="status_desc" sort={sort} onSortChange={onSortChange}
                filter={facets && onColFilterChange ? (
                  <HeaderFilter testid="filter-status"
                    options={(facets.statuses || []).map((s) => ({ value: s, label: STATUS_LABELS[s] || s }))}
                    selected={colFilters.statuses} onChange={(v) => onColFilterChange("statuses", v)} />
                ) : null} />
              <SortableTh label="Currently at" asc="location_asc" desc="location_desc" sort={sort} onSortChange={onSortChange}
                filter={facets && onColFilterChange ? (
                  <HeaderFilter testid="filter-location"
                    options={(facets.locations || []).map((l) => ({ value: l.id, label: l.label }))}
                    selected={colFilters.locations} onChange={(v) => onColFilterChange("locations", v)} />
                ) : null} />
              <SortableTh label="Last scanned" asc="scanned_asc" desc="scanned_desc" sort={sort} onSortChange={onSortChange} />
              <SortableTh label="Age" asc="age_asc" desc="age_desc" sort={sort} onSortChange={onSortChange} className="text-right" />
              <SortableTh label="Cycles" asc="cycles_asc" desc="cycles_desc" sort={sort} onSortChange={onSortChange} className="text-right" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.length === 0 && (
              <tr><td colSpan="10" className="px-4 py-10 text-center text-muted-foreground">{emptyMessage}</td></tr>
            )}
            {items.map((it, idx) => (
              <tr
                key={it.rfid}
                className={`hover:bg-neutral-50 cursor-pointer ${it.is_overdue ? "bg-red-50/40" : ""}`}
                onClick={() => onOpenDetail(it)}
              >
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(it.rfid)} onCheckedChange={() => toggle(it.rfid)} />
                </td>
                <td className="px-2 py-2 text-right font-mono text-xs text-neutral-500 tabular-nums" data-testid={`rfid-seq-${it.rfid}`}>
                  {startNo + idx + 1}
                </td>
                <td className="px-4 py-2 font-mono">{it.rfid}</td>
                <td className="px-4 py-2 max-w-[220px]">
                  {it.notes ? (
                    <span
                      className="inline-block truncate text-xs bg-amber-50 border border-amber-200 text-amber-900 px-2 py-0.5 rounded max-w-full align-middle"
                      title={it.notes}
                      data-testid={`rfid-note-${it.rfid}`}
                    >
                      {it.notes}
                    </span>
                  ) : (
                    <span className="text-xs text-neutral-300">—</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: it.product_color }} />
                    {it.product_name}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {it.is_missing ? (
                    <Badge className="bg-red-100 text-red-800 border border-red-200" data-testid={`rfid-missing-${it.rfid}`}>Missing</Badge>
                  ) : it.status === "at_warehouse" ? (
                    <Badge className="bg-green-100 text-green-800 border border-green-200">At warehouse</Badge>
                  ) : (
                    <Badge className={it.is_overdue
                      ? "bg-red-100 text-red-800 border border-red-200"
                      : "bg-amber-100 text-amber-800 border border-amber-200"}>
                      {it.is_overdue ? "Overdue" : "At hotel"}
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-2">
                  {it.status === "at_warehouse" ? "Warehouse" : (it.current_hotel_name || "—")}
                  {it.days_at_current_location !== null && it.days_at_current_location !== undefined && (
                    <span className="text-xs text-muted-foreground ml-2">({it.days_at_current_location}d)</span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs whitespace-nowrap" data-testid={`rfid-lastscan-${it.rfid}`}
                    title={it.last_dispatched_at ? it.last_dispatched_at.slice(0, 16).replace("T", " ") : "Never scanned"}>
                  {daysSince(it.last_dispatched_at) !== null
                    ? <span className={it.is_overdue ? "text-red-700 font-bold" : ""}>{daysSince(it.last_dispatched_at)}d</span>
                    : <span className="text-neutral-300">—</span>}
                </td>
                <td className="px-4 py-2 text-right font-mono text-sm">
                  {it.age_days !== null ? `${it.age_days}d` : "—"}
                </td>
                <td className="px-4 py-2 text-right font-mono text-sm">
                  {it.usage_count}×
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>← Prev</Button>
            <span className="px-2">Page {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>Next →</Button>
          </div>
        </div>
      )}
    </>
  );
}

function RfidDetailDialog({ rfid, onClose, onUpdated }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotes(rfid?.notes || ""); }, [rfid]);
  if (!rfid) return null;
  const saveNotes = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/linen/${rfid.rfid}`, { notes });
      toast.success("Notes saved");
      onUpdated && onUpdated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!rfid} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-brand-navy flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: rfid.product_color }} />
            {rfid.rfid}
          </DialogTitle>
          <DialogDescription>{rfid.product_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded border">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">First registered</p>
              <p className="font-medium">
                {rfid.created_at ? new Date(rfid.created_at).toLocaleDateString() : "—"}
              </p>
              <p className="text-xs text-muted-foreground">{rfid.age_days}d ago</p>
            </div>
            <div className="p-2 rounded border">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total cycles</p>
              <p className="font-display text-2xl font-bold text-brand-navy">{rfid.usage_count}×</p>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold">Notes</Label>
            <Textarea
              data-testid="rfid-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Supplier, material, condition..."
              rows={3}
            />
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1">
              <History className="h-3 w-3" /> Recent history
            </p>
            {(rfid.history || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No history yet.</p>
            ) : (
              (rfid.history || []).map((h, idx) => (
                <HistoryRow key={idx} h={h} />
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={saveNotes} disabled={saving} className="bg-brand-navy hover:bg-brand-navy-hover" data-testid="rfid-notes-save">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save notes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Stocktake — scan everything in the warehouse with an RFID gun, then commit.
// ============================================================================
function StocktakeView({ onUpdate }) {
  const [scanned, setScanned] = useState([]); // ordered, unique list of RFIDs scanned this session
  const [input, setInput] = useState("");
  const [summary, setSummary] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [showRecovered, setShowRecovered] = useState(false);
  const [showMatched, setShowMatched] = useState(false);
  const [showUnknown, setShowUnknown] = useState(false);
  const inputRef = useRef(null);

  // Refresh the preview whenever the scan list changes (debounced).
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoadingPreview(true);
      try {
        const res = await axios.post(`${API}/linen/stocktake/preview`, { rfids: scanned });
        setSummary(res.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load summary");
      } finally {
        setLoadingPreview(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [scanned]);

  const addRfid = (raw) => {
    const code = String(raw || "").trim();
    if (!code) return;
    setScanned((prev) => {
      if (prev.includes(code)) {
        toast.info(`Already scanned: ${code}`);
        return prev;
      }
      return [...prev, code];
    });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const code = input.trim();
      if (!code) return;
      addRfid(code);
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const removeOne = (rfid) => setScanned((prev) => prev.filter((r) => r !== rfid));

  // Native PDA wrapper streams EPCs here too — every scanned tag joins the
  // session list and the existing preview-debounce refreshes the summary.
  usePdaScan((epcs) => {
    setScanned((prev) => {
      const seen = new Set(prev);
      const additions = epcs.filter((e) => !seen.has(e));
      return additions.length ? [...prev, ...additions] : prev;
    });
  });

  // Bottom-strip Reset wipes our scanned[] too, not just the breakdown.
  usePdaScanReset(() => setScanned([]));

  const resetAll = () => {
    setScanned([]);
    setResetConfirm(false);
    setInput("");
    inputRef.current?.focus();
  };

  const handleCommit = async () => {
    if (!summary || summary.recovered_from_hotel_count === 0) {
      toast.info("No status changes to apply");
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/linen/stocktake/commit`, { rfids: scanned });
      toast.success(
        `Stocktake committed — ${res.data.flipped_count} item(s) flipped to warehouse`,
        {
          description: `Missing: ${res.data.summary.missing_warehouse_count} · Unknown: ${res.data.summary.unknown_count}`,
        },
      );
      const fresh = await axios.post(`${API}/linen/stocktake/preview`, { rfids: scanned });
      setSummary(fresh.data);
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to commit");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="stocktake-view">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl text-brand-navy flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" /> Warehouse Stocktake
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Walk the warehouse with your RFID gun. Scan everything you see — the system reconciles totals,
            spots items missing from the warehouse, and flips at-hotel items found here back to "at warehouse".
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Scan input — hidden inside the PDA wrapper so the soft keyboard
              doesn't pop up every time a scan completes. Operator just pulls
              the trigger and watches the stats below. */}
          {isInPdaWrapper() ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm flex items-center justify-between">
              <span className="text-emerald-800">
                <ScanLine className="h-4 w-4 inline mr-1.5 align-text-bottom" />
                Pull trigger to add tags · {scanned.length} scanned
              </span>
              <Button
                data-testid="stocktake-reset-btn"
                variant="outline"
                size="sm"
                onClick={() => (scanned.length > 0 ? setResetConfirm(true) : resetAll())}
                disabled={scanned.length === 0}
              >
                <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
              </Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1">
                <Label className="text-[11px] uppercase tracking-wider font-semibold">Scan RFID</Label>
                <Input
                  ref={inputRef}
                  data-testid="stocktake-scan-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder="Pull trigger — tags stream in automatically"
                  autoFocus
                  className="font-mono"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="stocktake-add-btn"
                  onClick={() => {
                    const c = input.trim();
                    if (c) { addRfid(c); setInput(""); inputRef.current?.focus(); }
                  }}
                  className="bg-brand-navy hover:bg-brand-navy-hover flex-1 sm:flex-none"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Add
                </Button>
                <Button
                  data-testid="stocktake-reset-btn"
                  variant="outline"
                  onClick={() => (scanned.length > 0 ? setResetConfirm(true) : resetAll())}
                  disabled={scanned.length === 0}
                  className="flex-1 sm:flex-none"
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
                </Button>
              </div>
            </div>
          )}

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBlock testid="stocktake-stat-scanned" label="Scanned total" value={scanned.length} tone="navy" icon={ScanLine} />
            <StatBlock testid="stocktake-stat-matched" label="Matched in warehouse" value={summary?.matched_warehouse_count ?? 0} tone="green" icon={CheckCircle2} onClick={() => setShowMatched((v) => !v)} />
            <StatBlock testid="stocktake-stat-recovered" label="At-hotel → recovered" value={summary?.recovered_from_hotel_count ?? 0} tone="amber" icon={ScanLine} onClick={() => setShowRecovered((v) => !v)} hint="Will flip to warehouse on commit" />
            <StatBlock testid="stocktake-stat-missing" label="Missing from warehouse" value={summary?.missing_warehouse_count ?? 0} tone="red" icon={AlertTriangle} onClick={() => setShowMissing((v) => !v)} hint="Expected at_warehouse but not scanned" />
          </div>
          {summary?.unknown_count > 0 && (
            <button
              data-testid="stocktake-unknown-btn"
              onClick={() => setShowUnknown((v) => !v)}
              className="w-full text-left text-xs px-3 py-2 rounded border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors"
            >
              ⚠ {summary.unknown_count} unknown RFID(s) scanned — not yet registered. Click to {showUnknown ? "hide" : "show"}.
            </button>
          )}

          {/* Commit button */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="text-xs text-muted-foreground">
              {loadingPreview ? "Updating summary…" : (summary ? `Reconciled against ${summary.known_total} known items` : "—")}
            </div>
            <Button
              data-testid="stocktake-commit-btn"
              onClick={handleCommit}
              disabled={committing || !summary || summary.recovered_from_hotel_count === 0}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {committing
                ? "Applying…"
                : (summary?.recovered_from_hotel_count
                  ? `Apply changes (${summary.recovered_from_hotel_count} flip${summary.recovered_from_hotel_count === 1 ? "" : "s"})`
                  : "No status changes")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Expandable detail lists */}
      {showMatched && summary?.matched_warehouse?.length > 0 && (
        <StocktakeList testid="stocktake-list-matched" title="Matched · already at warehouse" tone="green" items={summary.matched_warehouse} onClose={() => setShowMatched(false)} showHotel={false} />
      )}
      {showRecovered && summary?.recovered_from_hotel?.length > 0 && (
        <StocktakeList testid="stocktake-list-recovered" title="Recovered from hotels (will flip on commit)" tone="amber" items={summary.recovered_from_hotel} onClose={() => setShowRecovered(false)} showHotel />
      )}
      {showMissing && summary?.missing_warehouse?.length > 0 && (
        <StocktakeList testid="stocktake-list-missing" title="Missing from warehouse" tone="red" items={summary.missing_warehouse} onClose={() => setShowMissing(false)} showHotel={false} />
      )}
      {showUnknown && summary?.unknown?.length > 0 && (
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-display text-base text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Unknown RFIDs ({summary.unknown.length})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowUnknown(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="max-h-[40vh] overflow-y-auto font-mono text-xs space-y-1">
              {summary.unknown.map((r) => (
                <div key={r} className="px-2 py-1 bg-amber-50 border border-amber-200 rounded">{r}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scan history */}
      {scanned.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <ScanLine className="h-4 w-4" /> Scan history · {scanned.length}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[30vh] overflow-y-auto">
              <div className="flex flex-wrap gap-1.5">
                {[...scanned].reverse().map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-mono border bg-white"
                  >
                    {r}
                    <button
                      data-testid={`stocktake-remove-${r}`}
                      onClick={() => removeOne(r)}
                      className="text-muted-foreground hover:text-red-700"
                      title="Remove from scan list"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reset confirm */}
      <AlertDialog open={resetConfirm} onOpenChange={setResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset scan list?</AlertDialogTitle>
            <AlertDialogDescription>
              {scanned.length} RFID(s) will be removed from this session. Status changes you've already committed are NOT reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={resetAll} data-testid="stocktake-reset-confirm" className="bg-red-700 text-white hover:bg-red-800">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatBlock({ label, value, tone, icon: Icon, onClick, hint, testid }) {
  const toneCls = {
    navy:  "border-brand-navy/30 bg-brand-sky/5",
    green: "border-emerald-300 bg-emerald-50",
    amber: "border-amber-300 bg-amber-50",
    red:   "border-red-300 bg-red-50",
  }[tone] || "";
  const txtCls = {
    navy:  "text-brand-navy",
    green: "text-emerald-700",
    amber: "text-amber-700",
    red:   "text-red-700",
  }[tone] || "text-foreground";
  const clickable = !!onClick;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={!clickable}
      className={`text-left rounded-lg border-2 p-3 transition-all ${toneCls} ${clickable ? "hover:shadow-sm cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-3xl font-display font-bold mt-1 ${txtCls}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </button>
  );
}

function StocktakeList({ title, tone, items, onClose, showHotel, testid }) {
  const headTone = {
    green: "text-emerald-800",
    amber: "text-amber-900",
    red:   "text-red-800",
  }[tone] || "";
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className={`font-display text-base flex items-center gap-2 ${headTone}`}>
          {title} <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="max-h-[40vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5">RFID</th>
                <th className="text-left py-1.5">Product</th>
                {showHotel && <th className="text-left py-1.5">Current hotel</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.rfid} className="border-b last:border-b-0">
                  <td className="py-1.5 font-mono">{it.rfid}</td>
                  <td className="py-1.5">{it.product_name || "—"}</td>
                  {showHotel && <td className="py-1.5">{it.current_hotel_name || "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Identify — small-batch lookup. Operator scans one (or a few) RFIDs to see
// full history: current location, total cycles, last dispatch, recent events,
// notes. Useful when a returned item is dirty / damaged and you want to know
// which hotel it came from.
// ============================================================================
function IdentifyView({ onUpdate }) {
  const [input, setInput] = useState("");
  const [recent, setRecent] = useState([]); // array of items, newest first; max 8
  const [active, setActive] = useState(null); // currently displayed item
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const lookup = async (raw) => {
    const code = String(raw || "").trim();
    if (!code) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/linen/${encodeURIComponent(code)}`);
      const item = res.data;
      setActive(item);
      setRecent((prev) => {
        const filtered = prev.filter((p) => p.rfid !== item.rfid);
        return [item, ...filtered].slice(0, 8);
      });
    } catch (e) {
      if (e?.response?.status === 404) {
        setError({ rfid: code, message: `Unknown RFID — "${code}" is not registered.` });
        setActive(null);
      } else {
        toast.error(e?.response?.data?.detail || "Lookup failed");
      }
    } finally {
      setLoading(false);
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      lookup(input);
    }
  };

  // Native PDA wrapper streams EPCs here — identify just one tag at a time
  // so we look up the LAST EPC of the batch.
  usePdaScan((epcs) => {
    const last = epcs[epcs.length - 1];
    if (last) lookup(last);
  });

  return (
    <div className="space-y-4" data-testid="identify-view">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl text-brand-navy flex items-center gap-2">
            <Search className="h-5 w-5" /> Identify Linen
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Scan a single RFID (or type one) to instantly see its full history — current location, total cycles, last delivery, recent events.
            Great for inspecting a stained or damaged item to see which hotel it came back from.
          </p>
        </CardHeader>
        <CardContent>
          {isInPdaWrapper() ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm flex items-center gap-2">
              <ScanLine className="h-4 w-4 text-emerald-700" />
              <span className="text-emerald-800">
                Pull trigger — last tag's details appear below
              </span>
              {loading && <span className="text-xs text-emerald-700 ml-auto">looking up…</span>}
            </div>
          ) : (
          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <div className="flex-1">
              <Label className="text-[11px] uppercase tracking-wider font-semibold">Scan RFID</Label>
              <Input
                ref={inputRef}
                data-testid="identify-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pull trigger — auto-identifies last tag"
                autoFocus
                className="font-mono"
                disabled={loading}
              />
            </div>
            <Button
              data-testid="identify-lookup-btn"
              onClick={() => lookup(input)}
              disabled={loading || !input.trim()}
              className="bg-brand-navy hover:bg-brand-navy-hover"
            >
              <Search className="h-4 w-4 mr-1.5" /> {loading ? "Looking up…" : "Look up"}
            </Button>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Unknown banner */}
      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <div className="font-display font-bold">Not found</div>
                <div className="text-sm">{error.message}</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setError(null)} className="text-red-800">
              <X className="h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active item detail */}
      {active && (
        <IdentifyDetailCard
          item={active}
          onUpdate={() => {
            onUpdate?.();
            // Refetch to keep view in sync with new notes
            lookup(active.rfid);
          }}
        />
      )}

      {/* Recent scans */}
      {recent.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <History className="h-4 w-4" /> Recently identified ({recent.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((r) => {
                const isActive = active?.rfid === r.rfid;
                const statusTone =
                  r.status === "at_warehouse" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                  r.status === "at_hotel"     ? "bg-amber-100 text-amber-800 border-amber-200" :
                                                "bg-neutral-100 text-neutral-700 border-neutral-200";
                return (
                  <button
                    key={r.rfid}
                    data-testid={`identify-recent-${r.rfid}`}
                    onClick={() => setActive(r)}
                    className={`text-left rounded-lg border-2 p-3 transition-all ${
                      isActive
                        ? "border-brand-navy bg-brand-sky/10"
                        : "border-neutral-200 bg-white hover:border-brand-navy/40"
                    }`}
                  >
                    <div className="font-mono text-xs truncate">{r.rfid}</div>
                    <div className="font-display text-sm font-semibold mt-1 truncate">{r.product_name || "—"}</div>
                    <div className="flex items-center justify-between mt-1.5">
                      <Badge className={`text-[10px] ${statusTone}`}>
                        {r.status === "at_warehouse" ? "Warehouse" :
                         r.status === "at_hotel" ? (r.current_hotel_name || "Hotel") :
                         r.status}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{r.usage_count}× cycles</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function IdentifyDetailCard({ item, onUpdate }) {
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNotes(item.notes || ""); }, [item.rfid, item.notes]);

  const saveNotes = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/linen/${item.rfid}`, { notes });
      toast.success("Notes saved");
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = item.status === "at_warehouse"
    ? { label: "At warehouse", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" }
    : item.status === "at_hotel"
    ? { label: `At hotel · ${item.current_hotel_name || "—"}`, cls: "bg-amber-100 text-amber-800 border-amber-300" }
    : { label: item.status, cls: "bg-neutral-100 text-neutral-800 border-neutral-300" };

  return (
    <Card className="border-2 border-brand-navy" data-testid="identify-detail-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="font-display text-2xl text-brand-navy flex items-center gap-2 truncate">
              <span className="w-2 h-2 rounded-full" style={{ background: item.product_color || "#3b82f6" }} />
              <span className="font-mono">{item.rfid}</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-0.5">{item.product_name || "—"}</p>
          </div>
          <Badge className={`text-xs ${statusBadge.cls}`}>{statusBadge.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <DetailStat label="Total cycles" value={`${item.usage_count}×`} tone="navy" />
          <DetailStat
            label="Last dispatched"
            value={item.last_dispatched_at ? new Date(item.last_dispatched_at).toLocaleDateString() : "—"}
            sub={item.last_dispatched_at ? `${item.days_at_current_location ?? "?"}d ago` : ""}
          />
          <DetailStat
            label="First registered"
            value={item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}
            sub={item.age_days != null ? `${item.age_days}d ago` : ""}
          />
          <DetailStat
            label="Overdue"
            value={item.is_overdue ? "YES" : "No"}
            tone={item.is_overdue ? "red" : undefined}
          />
        </div>

        {/* Notes */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold">Notes</Label>
          <Textarea
            data-testid="identify-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Supplier, material, condition, complaint…"
            rows={2}
          />
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              data-testid="identify-notes-save"
              onClick={saveNotes}
              disabled={saving || notes === (item.notes || "")}
              className="bg-brand-navy hover:bg-brand-navy-hover"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save notes"}
            </Button>
          </div>
        </div>

        {/* History */}
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1">
            <History className="h-3 w-3" /> Recent history
          </p>
          {(item.history || []).length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No history yet — this item hasn't been dispatched.</p>
          ) : (
            <div className="space-y-1.5">
              {(item.history || []).map((h, idx) => (
                <HistoryRow key={idx} h={h} />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DetailStat({ label, value, sub, tone }) {
  const toneCls = tone === "navy" ? "text-brand-navy"
    : tone === "red" ? "text-red-700"
    : "text-foreground";
  return (
    <div className="p-3 rounded border bg-white">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-display text-xl font-bold ${toneCls} mt-0.5`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function HistoryRow({ h }) {
  // Backward compatible: dispatch uses `event` + `timestamp`, stocktake uses `type` + `at`.
  const evtKey = h.event || h.type || "";
  const at = h.timestamp || h.at;
  const renderHeadline = () => {
    if (evtKey === "dispatched") return `→ Sent to ${h.hotel_name || "—"}`;
    if (evtKey === "delivered")  return `→ Delivered to ${h.hotel_name || "—"}`;
    if (evtKey === "received")   return `← Received from ${h.from_hotel_name || "—"}`;
    if (evtKey === "reverted")   return `↺ Reverted from ${h.from_hotel_name || "—"}`;
    if (evtKey === "stocktake_recovered") return `📋 Stocktake — recovered`;
    if (evtKey === "re_registered") return `🏷 ${h.details || "Re-registered"}`;
    if (evtKey === "stocktake_scan") {
      return h.stocktake_type === "hotel" && h.hotel_name
        ? `📋 Hotel stocktake — ${h.hotel_name}`
        : `📋 Warehouse stocktake — scanned`;
    }
    if (evtKey === "rewash_in")  return `♻ Returned for rewash${h.from_hotel_name ? ` from ${h.from_hotel_name}` : ""}`;
    return evtKey || "Event";
  };
  return (
    <div className="border rounded-md px-3 py-2 bg-neutral-50">
      <div className="flex justify-between items-start gap-2 text-xs">
        <span className="font-semibold">{renderHeadline()}</span>
        <span className="text-muted-foreground whitespace-nowrap">
          {at ? new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
        </span>
      </div>
      {h.order_number && <p className="text-[11px] text-muted-foreground mt-0.5">Order {h.order_number}</p>}
      {h.details && <p className="text-[11px] text-muted-foreground mt-0.5">{h.details}</p>}
      {h.previous_hotel_name && evtKey === "stocktake_recovered" && (
        <p className="text-[11px] text-muted-foreground mt-0.5">Was at: {h.previous_hotel_name}</p>
      )}
    </div>
  );
}


